import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, FolderOpen, MessageCircle, CalendarPlus, MapPin, Smartphone } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { FadeUp } from "../components/Reveal";

const STATUS_STYLE = {
  pending: "bg-amber-100 text-amber-800",
  approved: "bg-emerald-100 text-emerald-800",
  declined: "bg-red-100 text-red-700",
  cancelled: "bg-slate-100 text-slate-500",
  completed: "bg-purple-100 text-purple-800",
};

export function StatusBadge({ status }) {
  return (
    <span className={`text-[11px] font-mono uppercase tracking-widest px-2.5 py-1 rounded-full ${STATUS_STYLE[status] || "bg-slate-100 text-slate-600"}`} data-testid={`status-badge-${status}`}>
      {status}
    </span>
  );
}

export default function PortalDashboard() {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    api.get("/bookings/mine").then((r) => setBookings(r.data)).catch(() => {});
    api.get("/notifications/mine").then((r) => setNotifications(r.data)).catch(() => {});
    api.get("/projects/mine").then((r) => setProjects(r.data)).catch(() => {});
  }, []);

  const upcoming = bookings.filter((b) => b.status === "approved").sort((a, b) => a.date.localeCompare(b.date))[0];
  const pending = bookings.filter((b) => b.status === "pending");

  return (
    <div data-testid="portal-dashboard">
      <FadeUp>
        <p className="text-xs font-mono uppercase tracking-[0.25em] text-purple-700">Client Portal</p>
        <h1 className="font-serif text-3xl sm:text-4xl mt-3 text-obsidian">
          Welcome back, <span className="italic text-purple-700">{user?.name?.split(" ")[0]}</span>
        </h1>
      </FadeUp>

      <div className="grid gap-6 lg:grid-cols-3 mt-10">
        <FadeUp className="lg:col-span-2 bg-obsidian text-white rounded-3xl p-8 grain relative overflow-hidden" data-testid="next-session-card">
          <p className="text-xs font-mono uppercase tracking-[0.25em] text-gold">Next Session</p>
          {upcoming ? (
            <div className="mt-5">
              <p className="font-serif text-3xl">{upcoming.service_name}</p>
              <p className="text-white/60 text-sm mt-2 flex items-center gap-2">
                <CalendarDays size={14} className="text-gold" /> {upcoming.date} at {upcoming.time}
              </p>
              <p className="text-white/60 text-sm mt-1.5 flex items-center gap-2">
                <MapPin size={14} className="text-gold" /> {upcoming.location_name}
              </p>
              {upcoming.admin_notes && <p className="text-xs text-white/40 mt-4 italic">Note from Empress: {upcoming.admin_notes}</p>}
            </div>
          ) : (
            <div className="mt-5">
              <p className="font-serif text-2xl text-white/80">Nothing confirmed yet</p>
              <Link to="/book" data-testid="dashboard-book-btn" className="inline-flex items-center gap-2 mt-5 px-6 py-3 rounded-full bg-gold text-obsidian text-sm font-semibold hover:brightness-110 transition-all">
                <CalendarPlus size={15} /> Request a session
              </Link>
            </div>
          )}
          {pending.length > 0 && (
            <p className="mt-6 text-xs font-mono text-amber-300/90 tracking-wide" data-testid="pending-count">
              {pending.length} request{pending.length > 1 ? "s" : ""} awaiting Empress's approval
            </p>
          )}
        </FadeUp>

        <FadeUp delay={0.1} className="bg-white rounded-3xl border border-purple-100 p-7" data-testid="quick-access-card">
          <p className="text-xs font-mono uppercase tracking-[0.25em] text-slate-400">Quick Access</p>
          <div className="mt-5 space-y-3">
            {[
              { to: "/portal/projects", icon: FolderOpen, label: `${projects.length} project${projects.length === 1 ? "" : "s"}`, sub: "Mixes, masters & stems" },
              { to: "/portal/bookings", icon: CalendarDays, label: `${bookings.length} booking${bookings.length === 1 ? "" : "s"}`, sub: "Upcoming & history" },
              { to: "/portal/messages", icon: MessageCircle, label: "Message Empress", sub: "Direct line to the studio" },
            ].map((q) => (
              <Link key={q.label} to={q.to} data-testid={`quick-${q.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="flex items-center gap-4 p-4 rounded-2xl border border-purple-100 hover:border-gold transition-colors">
                <span className="w-10 h-10 rounded-xl bg-lilac-mist flex items-center justify-center text-purple-700 shrink-0"><q.icon size={17} /></span>
                <span>
                  <span className="block text-sm font-medium text-obsidian">{q.label}</span>
                  <span className="block text-xs text-slate-400">{q.sub}</span>
                </span>
              </Link>
            ))}
          </div>
        </FadeUp>
      </div>

      <FadeUp delay={0.15} className="mt-6 bg-white rounded-3xl border border-purple-100 p-7" data-testid="sms-feed-card">
        <div className="flex items-center justify-between">
          <p className="text-xs font-mono uppercase tracking-[0.25em] text-slate-400 flex items-center gap-2">
            <Smartphone size={13} className="text-gold" /> Latest Texts
          </p>
          <Link to="/portal/notifications" className="text-xs text-purple-700 hover:text-gold transition-colors" data-testid="view-all-notifications">View all</Link>
        </div>
        <div className="mt-5 space-y-3">
          {notifications.slice(0, 3).map((n) => (
            <div key={n.notification_id} className="flex items-start gap-3">
              <span className="w-8 h-8 rounded-full bg-obsidian text-gold flex items-center justify-center shrink-0 text-[10px] font-mono">MBE</span>
              <div className="bg-lilac-mist rounded-2xl rounded-tl-sm px-4 py-3 text-sm text-slate-600 max-w-lg">{n.message}</div>
            </div>
          ))}
          {notifications.length === 0 && <p className="text-sm text-slate-400">No texts yet — they'll appear here after your first booking.</p>}
        </div>
      </FadeUp>
    </div>
  );
}
